import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"

import { privateRoutes } from "router/routes"

import { useBikeStoreProcess, useAlert } from "hooks"
import { OWNER_PRIVATE_GENDER, OWNER_COMPANY_GENDER, BIKE_BACK_STORE } from "utils/constants"
import dateService from "services/date"

// PDF
import { pdf } from "@react-pdf/renderer"
import saveBlob from "utils/saveBlob"
import SummaryBikePDF from "features/bike/Partials/Pdf/SummaryBikePDF"

type OwnerDescriptionProps = {
	className?: string
}

export const OwnerDescription: React.FC<OwnerDescriptionProps> = ({ className }) => {
	const { bikeProcessState } = useBikeStoreProcess()
	const { currentBike: bike } = bikeProcessState

	const { t } = useTranslation()
	const { alert } = useAlert()

	// We extract the link from the store-id
	const bikeTransferRequestStoreId = bike?.property_transfer_request?.store_id
	const bikeTransferRequestStoreLink = bikeTransferRequestStoreId?.startsWith("ST-")
		? bikeTransferRequestStoreId.slice(3)
		: null

	// Generate PDF summary
	const viewSummary = async (event: React.SyntheticEvent) => {
		event.preventDefault()

		// Manage pdf generation
		const timeStamp = dateService.getCurrentDate("dd-MM-y-HH'h'mm")
		const generateBikePDF =
			bike &&
			(await pdf(<SummaryBikePDF bike={bike} />)
				.toBlob()
				.then((blob) => {
					saveBlob(blob, `imprimer-recapitulatif-${bike?.code}-${timeStamp}.pdf`, {
						targetFile: false,
					})
					return blob
				})
				.catch((e) => null))

		if (!generateBikePDF) {
			alert("error", t("pdf.pdf_error", { ns: "bike" }))
		}
	}

	return (
		<div className={`w-full ${className}`}>
			<div className="c-card p-5 md:p-12 mb-5">
				<h2 className="c-h2 font-semibold mb-7 uppercase">
					{t("owner_title", { ns: "bike" })}
				</h2>
				{bike.bike_owner?.gender === OWNER_COMPANY_GENDER && (
					<p className="mb-2">
						<span className="text-black font-bold">
							{t("owner.raison_sociale", { ns: "bike" })} :{" "}
						</span>
						{bike.status === BIKE_BACK_STORE && bikeTransferRequestStoreLink && (
							<NavLink
								to={
									privateRoutes.partner.basePath +
									"/" +
									bikeTransferRequestStoreLink
								}
								className="underline font-bold"
							>
								{bike.bike_owner?.social_reason}
							</NavLink>
						)}
						{bike.status !== BIKE_BACK_STORE && !bikeTransferRequestStoreLink && (
							<span className="font-bold">{bike.bike_owner?.social_reason}</span>
						)}
					</p>
				)}
				{bike.bike_owner?.gender === OWNER_PRIVATE_GENDER && (
					<>
						<p className="mb-2">
							<span className="text-black font-bold">
								{t("owner.firstname", { ns: "bike" })} :{" "}
							</span>
							<span className="text-primary ml-2">{bike.bike_owner?.first_name}</span>
						</p>
						<p className="mb-2">
							<span className="text-black font-bold">
								{t("owner.name", { ns: "bike" })} :{" "}
							</span>
							<span className="text-primary ml-2">{bike.bike_owner?.last_name}</span>
						</p>
					</>
				)}
				<p className="mb-2">
					<span className="text-black font-bold">
						{t("owner.mail", { ns: "bike" })} :{" "}
					</span>
					<span className="text-primary ml-2">{bike.bike_owner?.mail}</span>
				</p>
				<p className="mb-7">
					<span className="text-black font-bold">
						{t("owner.phonenumber", { ns: "bike" })} :{" "}
					</span>
					<span className="text-primary ml-2">{bike.bike_owner?.phone}</span>
				</p>

				<button
					type="button"
					className="c-btn c-btn--primary-bordered px-7"
					onClick={viewSummary}
				>
					{t("cta_print_recap", { ns: "bike" })}
				</button>
			</div>
		</div>
	)
}
